import { Checkbox } from '@/common/ui/checkbox'
import { formatDate } from '@/common/utils/utils'
import { ONBOARDING_COPY } from '@/locales/en'
import type { ResolvedCheckboxItem } from './termsSections'

interface TermsAndConditionsSectionProps {
    title: string
    description?: string
    checkboxes: ResolvedCheckboxItem[]
    isCheckboxChecked: (checkboxId: string) => boolean
    onToggleCheckbox: (checkboxId: string) => void
}

/** Presentational: one titled terms section with zero or more independently-checked
 *  checkboxes below an optional description. Each checkbox tracks its own checked state,
 *  keyed by its own id — sections with multiple checkboxes (e.g. FinClear Terms and
 *  Conditions) never share state or a DOM id between rows. */
export function TermsAndConditionsSection({
    title,
    description,
    checkboxes,
    isCheckboxChecked,
    onToggleCheckbox,
}: TermsAndConditionsSectionProps) {
    return (
        <div className="flex flex-col gap-2 font-[Inter] text-base">
            <p className="font-medium text-content-primary">{title}</p>
            {description && <p className="text-content-secondary" dangerouslySetInnerHTML={{ __html: description }} />}
            {checkboxes.map((checkbox) => (
                <div className="flex items-start gap-4" key={checkbox.id}>
                    <Checkbox
                        id={checkbox.id}
                        checked={isCheckboxChecked(checkbox.id)}
                        onCheckedChange={() => onToggleCheckbox(checkbox.id)}
                        className="mt-1"
                    />
                    <div className="self-stretch inline-flex flex-col justify-start items-start gap-1.5">
                        <label
                            htmlFor={checkbox.id}
                            className="disclaimer-html"
                            dangerouslySetInnerHTML={{
                                __html: checkbox.label.replace('Privacy Policy', 'Privacy&nbsp;Policy'),
                            }}
                        />
                        {checkbox.updatedAt && (
                            <p className="text-content-tertiary text-sm">
                                {ONBOARDING_COPY.termsAndConditions.updatedLabel(formatDate(checkbox.updatedAt))}
                            </p>
                        )}
                    </div>
                </div>
            ))}
        </div>
    )
}
